import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import {
  Button,
  ComboboxItem,
  Modal,
  ScrollAreaAutosize,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconFileUpload,IconInfoCircle, IconUpload } from "@tabler/icons-react";
import i18n from "src/i18n";
import { z } from "zod";

import { getClinicFileTypes } from "@data/dropdownValues";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";

import { useCreateClinicFile } from "@hooks/mutations/clinic/useCreateClinicFile";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";

import { StyledAddFileModalContent } from "./AddFileModal.styled";


const formSchema = () =>
  z.object({
    tag: z.string().min(1, i18n.t("tag-required")),
    targetUser: z.string().optional(),
  });

type FormInputs = z.infer<ReturnType<typeof formSchema>>;

interface AddFileModalProps {
  opened: boolean;
  close: () => void;
}

export const AddFileModal = ({
  opened,
  close,
}: AddFileModalProps) => {
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const { data: doctorsData } = useGetUsers({ role: "doctor", accountOptions: true });
  const {
    mutate: createClinicFile,
    isLoading: isCreateClinicFileLoading,
  } = useCreateClinicFile();

  const doctorsComboboxItems = useMemo(() => {
    return doctorsData?.map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [doctorsData]);

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(formSchema()),
    initialValues: {
      tag: "",
      targetUser: "",
    },
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([
        ...uploadedFiles,
        ...acceptedFiles.map(file => {
          const blob = file.slice(0, file.size, file.type);
          const newFile = new File([blob], file.name, {
            type: file.type,
          });
          return newFile;
        }),
      ]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
    multiple: false,
    maxFiles: 1,
    maxSize: 3 * 1024 * 1024, // 3MB max
    disabled: uploadedFiles.length > 0,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleFormSubmit = (values: FormInputs) => {
    if (!uploadedFiles.length) {
      return;
    }

    const formData = new FormData();
    uploadedFiles.forEach(file => {
      formData.append("file", file);
    });
    formData.append("type", values.tag);
    formData.append("related_user", values.targetUser ?? "");

    createClinicFile(formData, {
      onSuccess: () => {
        form.reset();
        setUploadedFiles([]);
        close();
      },
    });
  };

  const handleClose = () => {
    form.reset();
    setUploadedFiles([]);
    close();
  };

  const fileTagOptions = getClinicFileTypes();

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("add-file")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
      size="md">
      <StyledAddFileModalContent>
        <form
          className="form"
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          
          <div className="form-field">
            <Select
              label={t("file-tag")}
              withAsterisk
              placeholder={t("select-file-tag")}
              data={fileTagOptions}
              {...form.getInputProps("tag")}
              key={form.key("tag")}
            />
          </div>

          <div className="form-field">
            <Select
              label={t("assign-to-doctor")}
              placeholder={t("select-doctor")}
              data={doctorsComboboxItems}
              {...form.getInputProps("targetUser")}
              key={form.key("targetUser")}
            />
          </div>

          <div className="files-wrapper">
            <div
              {...getRootProps({
                className: `dropzone ${isDragActive ? "active" : ""} ${
                  isDragAccept ? "accept" : ""
                } ${isDragReject ? "reject" : ""}`,
              })}>
              <input {...getInputProps()} />
              <div className="text-content">
                <div className="svg-wrapper">
                  <IconFileUpload size={28} />
                </div>
                <div className="p-wrapper">
                  <p>{t("drag-file-here")}</p>
                  <p>{t("or-click-to-browse-max")}</p>
                </div>
              </div>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="uploaded-files">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="uploaded-file">
                    <div className="file-info">
                      <div className="file-details">
                        <Text size="sm" fw={500}>{file.name}</Text>
                        <Text size="xs" c="dimmed">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </Text>
                      </div>
                      <Button
                        variant="subtle"
                        size="xs"
                        color="red"
                        onClick={() => removeFile(file)}>
                        {t("remove")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {uploadedFiles.length == 0 && (
              <div className="info-section">
                <div className="info-icon">
                  <IconInfoCircle size={20} />
                </div>
                <p className="info-text">
                 {t("file-appear-info")}
                </p>
              </div>
            )}
          </div>

          <div className="form-actions">
            <PermissionGate permission="clinic_files.create"><Button
              type="submit"
              loading={isCreateClinicFileLoading}
              disabled={isCreateClinicFileLoading || !uploadedFiles.length}>
              {t("submit")}
            </Button></PermissionGate>
          </div>
        </form>
      </StyledAddFileModalContent>
    </Modal>
  );
};
